import { ReactElement, useMemo, Fragment, forwardRef, useImperativeHandle } from 'react';
import {
  StandardLeaderPermissions, CategoryLeaderPermissions
} from '../../store';
import { getLeaderPermission } from '../../store/templates';
import { CabCheckbox } from '@CabComponents';
import { 
  Box, Divider, FormControl, FormLabel, Grid, List, ListItem, styled, Typography,
  Radio, RadioGroup, FormControlLabel 
} from '@mui/material';
import colors from '../../colors';
import { Control, Controller, UseFormSetValue, UseFormWatch, useForm } from 'react-hook-form';
import { DateTime } from 'luxon';
import { DependentController } from '../../utils/formUtils';
import UserPrefCabDatePicker from '@CabComponents/CabDatePicker/UserPrefDatePicker';
import { SharedLeaderGrant } from '../../store/cabinetApi/generated/appCore';
import { ProfileCategory } from '../../store/cabinetApi/generated/profiles';

interface PermissionsFormProps {
  grant?: SharedLeaderGrant;
  leaderProfileCategories: Record<number, ProfileCategory>;
  showUserSelector?: boolean;
  showDateRange?: boolean;
  /** When provided (e.g. in share flow), permission fields use parent form state so Share uses current values */
  parentControl?: Control<PermissionsFormData>;
  parentSetValue?: UseFormSetValue<PermissionsFormData>;
  parentWatch?: UseFormWatch<PermissionsFormData>;
  onSubmit: (data: PermissionsFormData) => void;
  onCancel: () => void;
}

export interface PermissionsFormData {
  startDate: string;
  endDate: string | null;
  schedulingPermissionEdit: boolean;
  schedulingPermissionAddToMeetings: boolean;
  crmAllowAllContacts: boolean;
  standardPermissions: StandardLeaderPermissions;
  customCategoryPermissions: CategoryLeaderPermissions;
  permissionType: 'all' | 'limited';
}

const permissionLabelMap: { [key in keyof StandardLeaderPermissions]: string } = {
  basic: 'Basic Info',
  airlines: 'Airlines',
  hotels: 'Hotels',
  personal: 'Family',
};

export interface PermissionsFormRef {
  submit: () => void;
}

export const PermissionsForm = forwardRef<PermissionsFormRef, PermissionsFormProps>(({
  grant,
  leaderProfileCategories,
  showUserSelector = false,
  showDateRange = true,
  parentControl,
  parentSetValue,
  parentWatch,
  onSubmit,
  onCancel
}, ref): ReactElement => {
  const isUsingParentForm = !grant && parentControl && parentSetValue && parentWatch;

  const defaultValues = useMemo(() => {
    if (grant) {
      // Edit mode - populate with existing grant data
      const schedulingPermissions = grant.scheduling_permissions;
      const standardPermissions: StandardLeaderPermissions = {
        basic: {
          view: grant.permissions.basic?.view || false,
          edit: grant.permissions.basic?.edit || false,
          delete: grant.permissions.basic?.delete || false,
        },
        airlines: {
          view: grant.permissions.airlines?.view || false,
          edit: grant.permissions.airlines?.edit || false,
          delete: grant.permissions.airlines?.delete || false,
        },
        hotels: {
          view: grant.permissions.hotels?.view || false,
          edit: grant.permissions.hotels?.edit || false,
          delete: grant.permissions.hotels?.delete || false,
        },
        personal: {
          view: grant.permissions.personal?.view || false,
          edit: grant.permissions.personal?.edit || false,
          delete: grant.permissions.personal?.delete || false,
        },
      };
      
      const customCategoryPermissions: CategoryLeaderPermissions = Object.values(leaderProfileCategories)
        .reduce((acc, c) => {
          const categoryPerms = grant.category_permissions[c.id];
          acc[c.id] = {
            view: categoryPerms?.view || false,
            edit: categoryPerms?.edit || false,
            delete: categoryPerms?.delete || false,
          };
          return acc;
        }, {} as CategoryLeaderPermissions);

      const crmContactPerms = grant.crm_contact_permissions || {};
      const crmAllowAllContacts = !!(
        crmContactPerms.view && crmContactPerms.change
      );

      const hasLimitedPermissions = !grant.is_admin && (
        Object.values(standardPermissions).some(p => !p.edit) ||
        Object.values(customCategoryPermissions).some(p => !p.edit) ||
        !schedulingPermissions.edit ||
        !crmAllowAllContacts
      );

      return {
        startDate: grant.start_date || DateTime.now().toISO(),
        endDate: grant.end_date || null,
        schedulingPermissionEdit: schedulingPermissions.edit || false,
        schedulingPermissionAddToMeetings: schedulingPermissions.add_to_meeting || false,
        crmAllowAllContacts,
        standardPermissions,
        customCategoryPermissions,
        permissionType: hasLimitedPermissions ? 'limited' as const : 'all' as const,
      };
    } else {
      // Create mode - use defaults
      return {
        startDate: DateTime.now().toISO(),
        endDate: null,
        schedulingPermissionEdit: false,
        schedulingPermissionAddToMeetings: true,
        crmAllowAllContacts: false,
        standardPermissions: {
          basic: getLeaderPermission(),
          airlines: getLeaderPermission(),
          hotels: getLeaderPermission(),
          personal: getLeaderPermission(),
        },
        customCategoryPermissions: Object.values(leaderProfileCategories)
          .map(c => ({ [c.id]: getLeaderPermission() }))
          .reduce((prev, cur) => ({ ...prev, ...cur }), {}),
        permissionType: 'all' as const,
      };
    }
  }, [grant, leaderProfileCategories]);

  const internalForm = useForm<PermissionsFormData>({ 
    values: defaultValues 
  });
  const {
    control: internalControl,
    setValue: internalSetValue,
    handleSubmit: internalHandleSubmit,
    watch: internalWatch,
  } = internalForm;

  const control = isUsingParentForm ? parentControl! : internalControl;
  const setValue = isUsingParentForm ? parentSetValue! : internalSetValue;
  const watch = isUsingParentForm ? parentWatch! : internalWatch;

  const currentPermissionType = watch('permissionType');

  const handleFormSubmit = (data: PermissionsFormData) => {
    onSubmit(data);
  };

  useImperativeHandle(ref, () => ({
    submit: () => {
      if (isUsingParentForm) {
        handleFormSubmit(
          watch() as PermissionsFormData
        );
      } else {
        internalHandleSubmit(handleFormSubmit)();
      }
    }
  }));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3
      }}>
      {showDateRange && (
        <Grid
          container
          sx={{
            marginTop: "16px",
            padding: 0
          }}>
          <Grid size={5}>
            <Controller name="startDate" control={control} render={({ field: { ref: fieldRef, ...field }}) => (
              <FormControl>
                <FormLabel>Start Date</FormLabel>
                <UserPrefCabDatePicker {...field} />
              </FormControl>
            )} />
          </Grid>
          <Grid size={2}/>
          <Grid size={5}>
            <Controller name="endDate" control={control} render={({ field: { ref: fieldRef, ...field }}) => (
              <FormControl>
                <FormLabel>End Date</FormLabel>
                <UserPrefCabDatePicker {...field} futureOnly clearable />
              </FormControl>
            )} />
          </Grid>
        </Grid>
      )}
      <Box>
        <Typography variant="h6" sx={{ marginTop: 2, marginBottom: 1, fontWeight: 'bold' }}>
          Permissions
        </Typography>
        <Typography variant="body1" sx={{ color: colors.black500, marginBottom: 2 }}>
          {grant ? 'Update permissions for this user' : 'Permissions for the selected user'}
        </Typography>
        <Controller name="permissionType" control={control} render={({ field: { ref: fieldRef, ...field } }) => (
          <FormControl component="fieldset">
            <RadioGroup {...field}>
              <FormControlLabel 
                value="all" 
                control={<Radio />} 
                label="Allow all permissions" 
              />
              <FormControlLabel 
                value="limited" 
                control={<Radio />} 
                label="Limit to specific permissions" 
              />
            </RadioGroup>
          </FormControl>
        )} />
        <Typography sx={{color: colors.black500, marginTop: 1}}>
          {currentPermissionType === 'all' 
            ? 'User will have all permissions you currently have for this teammate'
            : 'Select specific permissions below'
          }
        </Typography>
      </Box>
      {currentPermissionType === 'limited' && (
        <Box>
          <PermissionHeader>
            <ProfileSectionLabel>Scheduling</ProfileSectionLabel>
            <Box>
              <Box component="span" sx={{
                marginRight: "13px"
              }}>Edit</Box>
            </Box>
          </PermissionHeader>

          <List>
            <PermissionRow>
              <DependentController
                name="schedulingPermissionAddToMeetings"
                control={control}
                dependencies={["permissionType"]}
                render={({ field: { ref: fieldRef, ...field }, deps: [permissionType] }) => (
                  <>
                    <FormLabel slot="start">
                      Allow adding this person to meetings
                    </FormLabel>
                    <CabCheckbox slot='end'
                      {...field}
                      onChange={e => {
                        field.onChange(e);
                        if (!e.target.checked) {
                          setValue('schedulingPermissionEdit', false);
                        }
                      }}
                      checked={(field.value || permissionType === 'all') as boolean}
                      disabled={permissionType === 'all'}
                      sx={{ marginLeft: 3 }}
                    />
                  </>
                )}
              />
            </PermissionRow>

            <PermissionRow>
              <DependentController
                name="schedulingPermissionEdit"
                control={control}
                dependencies={["permissionType"]}
                render={({ field: { ref: fieldRef, ...field }, deps: [permissionType] }) => (
                  <>
                    <FormLabel slot="start">
                      Allow viewing/editing of all meetings
                    </FormLabel>
                    <CabCheckbox slot='end'
                      {...field}
                      onChange={e => {
                        field.onChange(e);
                        if (e.target.checked) {
                          setValue('schedulingPermissionAddToMeetings', true);
                        }
                      }}
                      checked={(field.value || permissionType === 'all') as boolean}
                      disabled={permissionType === 'all'}
                      sx={{ marginLeft: 3 }}
                    />
                  </>
                )}
              />
            </PermissionRow>
            <Divider/>
          </List>

          <PermissionHeader>
            <ProfileSectionLabel>CRM</ProfileSectionLabel>
            <Box/>
          </PermissionHeader>
          <List>
            <PermissionRow>
              <DependentController
                name="crmAllowAllContacts"
                control={control}
                dependencies={["permissionType"]}
                render={({ field: { ref: fieldRef, ...field }, deps: [permissionType] }) => (
                  <>
                    <FormLabel slot="start">
                      Allow viewing/editing of all contacts
                    </FormLabel>
                    <CabCheckbox slot='end'
                      {...field}
                      checked={(field.value || permissionType === 'all') as boolean}
                      disabled={permissionType === 'all'}
                      sx={{ marginLeft: 3 }}
                    />
                  </>
                )}
              />
            </PermissionRow>
            <Divider/>
          </List>

          <PermissionHeader>
            <ProfileSectionLabel>Profile</ProfileSectionLabel>
            <Box>
              <Box component="span" sx={{
                marginRight: "18px"
              }}>View</Box>
              <Box component="span" sx={{
                marginRight: "13px"
              }}>Edit</Box>
            </Box>
          </PermissionHeader>
          <List>
            {Object.entries(defaultValues.standardPermissions).map(([category, value]) => (
              <Fragment key={category}>
                <PermissionRow>
                  <FormLabel slot="start">
                    {permissionLabelMap[category as keyof StandardLeaderPermissions]}
                  </FormLabel>
                  <Box>
                    <DependentController
                      name={`standardPermissions.${category as keyof StandardLeaderPermissions}.view`}
                      control={control}
                      dependencies={["permissionType"]}
                      render={({ field: { ref: fieldRef, ...field }, deps: [permissionType] }) => (
                        <CabCheckbox slot='end'
                          {...field}
                          onChange={e => {
                            field.onChange(e);
                            if (!e.target.checked) {
                              setValue(
                                `standardPermissions.${category as keyof StandardLeaderPermissions}.edit`, false);
                            }
                          }}
                          checked={(field.value || permissionType === 'all') as boolean}
                          disabled={permissionType === 'all'}
                        />
                      )}
                    />
                    <DependentController
                      name={`standardPermissions.${category as keyof StandardLeaderPermissions}.edit`}
                      control={control}
                      dependencies={["permissionType"]}
                      render={({ field: { ref: fieldRef, ...field }, deps: [permissionType] }) => (
                        <CabCheckbox slot='end'
                          {...field}
                          onChange={e => {
                            field.onChange(e);
                            if (e.target.checked) {
                              setValue(
                                `standardPermissions.${category as keyof StandardLeaderPermissions}.view`, true);
                            }
                          }}
                          checked={(field.value || permissionType === 'all') as boolean}
                          disabled={permissionType === 'all'}
                          sx={{ marginLeft: 3 }}
                        />
                      )}
                    />
                  </Box>
                </PermissionRow>
                <Divider/>
              </Fragment>
            ))}

            {Object.keys(leaderProfileCategories).map(categoryId =>
              <Fragment key={categoryId}>
                <PermissionRow>
                  <FormLabel slot="start">
                    {leaderProfileCategories[Number(categoryId)].title}
                  </FormLabel>
                  <Box>
                    <DependentController
                      name={`customCategoryPermissions.${categoryId}.view`}
                      control={control}
                      dependencies={["permissionType"]}
                      render={({ field: { ref: fieldRef, ...field }, deps: [permissionType] }) => (
                        <CabCheckbox slot='end'
                          {...field}
                          onChange={e => {
                            field.onChange(e);
                            if (!e.target.checked) {
                              setValue(`customCategoryPermissions.${categoryId}.edit`, false);
                            }
                          }}
                          checked={(field.value || permissionType === 'all') as boolean}
                          disabled={permissionType === 'all'}
                        />
                      )}
                    />
                    <DependentController
                      name={`customCategoryPermissions.${categoryId}.edit`}
                      control={control}
                      dependencies={["permissionType"]}
                      render={({ field: { ref: fieldRef, ...field }, deps: [permissionType] }) => (
                        <CabCheckbox slot='end'
                          {...field}
                          onChange={e => {
                            field.onChange(e);
                            if (e.target.checked) {
                              setValue(`customCategoryPermissions.${categoryId}.view`, true);
                            }
                          }}
                          checked={(field.value || permissionType === 'all') as boolean}
                          disabled={permissionType === 'all'}
                          sx={{ marginLeft: 3 }}
                        />
                      )}
                    />
                  </Box>
                </PermissionRow>
                <Divider/>
              </Fragment>
            )}
          </List>
        </Box>
      )}
    </Box>
  );
});

const ProfileSectionLabel = styled("span", { label: "ProfileSectionLabel" })({
  fontWeight: "bold",
  marginLeft: "15px",
});

const PermissionHeader = styled(Box, { label: "PermissionHeader" })({
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "5px",
});

const PermissionRow = styled(ListItem, { label: "PermissionRow" })({
  display: "flex",
  justifyContent: "space-between"
});

PermissionsForm.displayName = 'PermissionsForm';

export default PermissionsForm;
